import dayjs from 'dayjs';
import type { BookingEvent } from '@/lib/api.ts';

/**
 * Venue-local date label for a multi-day event's date pill, e.g.
 * "Thu, Nov 19 - Sat, Nov 21". Uses the API's pre-formatted venue-timezone
 * DateFormats (`date_dow_short` + `date_short`) so the range never shifts
 * with the browser's timezone.
 *
 * Returns null unless the event GENUINELY spans multiple calendar days.
 * `datetime_local_end__formatted` is the authoritative signal — the API
 * returns it non-null only for real multi-day spans
 * (`Event::getDatetimeLocalEndFormattedAttribute`, which itself delegates to
 * `Event::isMultiDay`). The overnight-rollover trap is why the raw `date_end`
 * column is never consulted here: a 9 PM -> 2 AM one-nighter gets its
 * `date_end` auto-rolled to the next calendar day on save
 * (`Event::handleEndDateAdjustment`), so `date_end !== date` would wrongly
 * classify every after-midnight event as multi-day. member/utils/eventDateRange.ts
 * applies the same rule against the same API contract.
 */
export const formatEventDateRange = (event: BookingEvent): string | null => {
  const { datetime_local__formatted: start, datetime_local_end__formatted: end } = event;

  if (!start?.date_dow_short || !start.date_short || !end?.date_dow_short || !end.date_short) {
    return null;
  }

  return `${start.date_dow_short}, ${start.date_short} - ${end.date_dow_short}, ${end.date_short}`;
};

const parseEventDate = (value?: string) => {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export interface DatePillText {
  /** Grouping/event name shown above the label, when it adds context. */
  title: string | null;
  /** The pill's primary text: a date range, a single date, or a name fallback. */
  label: string;
}

/**
 * Derives the date pill's text for the Booking date strip. Returns a nullable
 * `title` (the name shown above the date) and a required `label` (the date or
 * fallback). `title` exists only when there is a date for it to sit above —
 * when there's no parseable date the name IS the label, never a title.
 *
 * Single-day events suppress a `title` that just duplicates the weekday
 * ("Friday" over "Fri, Nov 20"). Multi-day events keep the name
 * unconditionally — a range never spells out a single weekday, so a name like
 * "SAGA Race Club" is never redundant there.
 */
export const getDatePillText = (event: BookingEvent): DatePillText => {
  const eventDate = parseEventDate(event.date);
  const dateRange = formatEventDateRange(event);
  const dateLabel = dateRange ?? eventDate?.format('ddd, MMM D') ?? null;

  if (dateLabel === null) {
    return { title: null, label: event.name || 'Date TBD' };
  }

  // Weekday-duplicate suppression applies to single-day events only: a range
  // never spells out one weekday, so a multi-day name ("Thursday") is never
  // redundant. eventDate is guaranteed non-null whenever there's no dateRange
  // (dateLabel would be null otherwise), so the format call below is safe.
  const isWeekdayDuplicate =
    dateRange === null &&
    eventDate !== null &&
    event.name.toLowerCase() === eventDate.format('dddd').toLowerCase();
  const title = event.name && !isWeekdayDuplicate ? event.name : null;

  return { title, label: dateLabel };
};
